/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as React from "react";
import MediaQuery from "react-responsive";

import {observer} from "mobx-react";
import {autorun, reaction} from "mobx";

import "./app.scss";

import {AppConfiguration} from "../../core/config/app-configuration";
import {ModuleConfiguration} from "../../core/config/module-configuration";
import {MapViewerConfiguration} from "../../config/map-viewer-configuration";
import {MessdatenConfiguration} from "../../config/messdaten-configuration";

import {ErrorBoundary} from "../../core/components/error-boundary/error-boundary";
import esriConfig from "@arcgis/core/config";

import {MapState} from "../../core/map-state";
import {UiState} from "../../core/ui-state";
import {Kiwis} from "../../kiwis";
import {LayerStack} from "./map/layer-stack/layer-stack";
import {AppState} from "./app-mobx";

import {Header} from "./header/header";
import {Footer} from "./footer/footer";
import {Maintenance} from "../../core/components/maintenance/maintenance";

import {StationPopup} from "./station-popup/station-popup";
import {KiwisStatus} from "./kiwis-status/kiwis-status";

import {GeoProxy} from "../../core/geo-proxy";
import {MessageProps, Sidebar as SemanticSidebar} from "semantic-ui-react";

import {BasemapGallery} from "../../core/components/basemap-gallery/basemap-gallery";
import {Sidebar} from "./sidebar/sidebar";

import {TopicConfiguration, TopicLayerConfiguration} from "../../config/topic-configuration";
import {MessageBox} from "../../core/components/message-box/message-box";
import MessageBlock from "../../core/components/message-block/message-block";
import {Popup} from "../../core/components/popup/popup";
import IdentityManager from "@arcgis/core/identity/IdentityManager";
import {NavigateFunction} from "react-router";
import CookieBanner from "../../core/components/cookie-banner/cookie-banner";
import SVG from "react-inlinesvg";
import crossIcon from "../../assets/images/layout/cross.svg";
import EsriPoint from "@arcgis/core/geometry/Point";
import EsriGraphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import EsriCompass from "@arcgis/core/widgets/Compass";
import EsriLocate from "@arcgis/core/widgets/Locate";
import {SearchSourceConfiguration, SearchSourceQueryConfiguration} from "../../core/config/search-configuration";
import {
    FillSymbolConfiguration,
    PictureMarkerSymbolConfiguration
} from "../../core/config/symbol-configuration";

import {Measurement} from "../../core/components/tools/measurement/measurement";
import {ElevationProfile} from "../../core/components/tools/elevation-profile/elevation-profile";
import MessageStartup from "../../core/components/message-startup/message-startup";
import {TopicSwitcher} from "../../core/components/topic-switcher/topic-switcher";
import {MessageConfiguration} from "../../core/config/message-configuration";
import * as arcade from "@arcgis/core/arcade";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

import {StationFinder} from "../../kiwis/station-finder";
/* Lazy load Webmap to show loader while importing */
const WebMap = React.lazy(() =>
    import("../../core/geo").then((module) => ({ default: module.WebMap }))
);

export interface AppProps {
    config: AppConfiguration;
    moduleConfig: ModuleConfiguration;
    appStore: AppState;
    location: Location;
    navigate: NavigateFunction;
    params: any;
    searchParams: URLSearchParams;
    setSearchParams: () => void;
}

/**
 * Anwendungsklasse
 */
@observer
export class App extends React.Component<AppProps, any> {
    autorunDisposers: any[] = [];
    appStore: AppState;
    uiStore: UiState;
    mapStore: MapState;
    kiwis: Kiwis;

    messdatenConfig: MessdatenConfiguration;
    mapViewerConfig: MapViewerConfiguration;
    appConfig: AppConfiguration;

    appUiRef = React.createRef<HTMLDivElement>();

    stopBrowserNavigationListener: any;
    disposeStationPopupCloser: any;
    focusArgs: number[] = [];

    constructor(props: AppProps) {
        super(props);
        this.appStore = this.props.appStore;
        this.uiStore = new UiState();
        this.mapStore = new MapState();
        this.state = {
            northFacing: true,
            alertMessage: [],
            startupMessage: [],
            addClass: []
        };

        this.uiStore.setUiSize(window.innerWidth, window.innerHeight);

        this.messdatenConfig = props.moduleConfig as MessdatenConfiguration;
        this.mapViewerConfig = this.messdatenConfig as MapViewerConfiguration;
        if (!this.messdatenConfig) {
            // eslint-disable-next-line no-console
            console.error("Messdaten configuration undefined.");
        }

        this.kiwis = new Kiwis(this.messdatenConfig.kiwis);
        this.appConfig = props.config as AppConfiguration;

        // load stations, stations layer is updated asynchronously by mobx
        this.kiwis.loadStations();

        // configure proxy page
        const geoProxy = new GeoProxy(this.appConfig.proxy);
        geoProxy.loadRules();
        esriConfig.request.proxyUrl = this.appConfig.proxy.url;

        // configure assets path
        if (this.appConfig.assetsPath) {
            esriConfig.assetsPath = this.appConfig.assetsPath;
        }
        // configure fonts URL
        if (this.appConfig.fontsUrl) {
            esriConfig.fontsUrl = this.appConfig.fontsUrl;
        }
        // configure GeometryService URL
        if (this.appConfig.geometryServiceUrl) {
            esriConfig.geometryServiceUrl = this.appConfig.geometryServiceUrl;
        }
        // configure portal
        if (this.appConfig.portalUrl) {
            esriConfig.portalUrl = this.appConfig.portalUrl;
            // Esri Anmeldedialog unterdrücken
            // eslint-disable-next-line max-len
            // https://community.esri.com/t5/arcgis-api-for-javascript-questions/detect-or-prevent-the-arcgis-authentication-dialog/td-p/1047565
            IdentityManager.on("dialog-create", () => {
                // eslint-disable-next-line no-console
                console.warn("Ungenügende Berechtigung (IdentityManager dialog-create)");
                IdentityManager.dialog.destroy();
            });
        } else {
            // eslint-disable-next-line no-console
            console.warn("No portal connection defined.");
        }
        this.mapStore.setAppConfig(this.appConfig);
        this.setTopic();

        this.handleMapLoad = this.handleMapLoad.bind(this);
        this.handleBrowserNavigation = this.handleBrowserNavigation.bind(this);
        this.handleResize = this.handleResize.bind(this);
    }

    /**
     * Load the default topic
     */
    loadDefaultTopic(): void {
        // invalid topic id set on url --> start with default
        const topic = this.messdatenConfig.getTopicByRoute("/", this.messdatenConfig.subtopics);
        if (!topic) {
            const msg = "No default topic found in config. Please add '/' for one of the topics in routes array";
            // eslint-disable-next-line no-console
            console.error(msg);
            throw new Error(msg);
        }
        this.appStore.setSubtopic(topic);
    }

    /**
     * Extract route from url and find topic by route
     * Set topic when route matches a topic
     */
    setTopicFromUrl(): void {
        if (this.props.params && this.props.params.subtopic) {
            const topicRoute = this.props.params.subtopic;
            // get topicId from url and set to mobx store
            const topic = this.mapViewerConfig.getTopicByRoute(`/${topicRoute}`, this.mapViewerConfig.subtopics);
            if(topic) {
                this.appStore.setSubtopic(topic);
            } else {
                this.loadDefaultTopic();
            }
        } else {
            this.loadDefaultTopic();
        }
    }
    setTopic(): void {
        const defaultTopic = this.mapViewerConfig.getTopicByRoute("/");
        if (!defaultTopic) {
            const msg = "No default topic found in config. Please add '/' for one of the topics in routes array";
            // eslint-disable-next-line no-console
            console.error(msg);
            throw new Error(msg);
        }
        // Zufälliger Extent aus Bookmarks, falls weder center noch extent definiert wurde
        if(!this.appConfig.mapView.center && !this.appConfig.mapView.extent
            && !this.appConfig.mapView.viewpoint) {
            const {bookmarks} = this.mapViewerConfig;
            const bookmark = bookmarks[Math.floor(Math.random() * bookmarks.length)];
            if(bookmark?.viewpoint) {
                delete this.appConfig.mapView.center;
                delete this.appConfig.mapView.zoom;
                this.appConfig.mapView.viewpoint = bookmark.viewpoint;
                if(this.appConfig.mapView.scale) {
                    this.appConfig.mapView.viewpoint.scale = this.appConfig.mapView.scale;
                    delete this.appConfig.mapView.scale;
                }
            }
        }
        this.appStore.setTopic(defaultTopic);
    }

    /**
     * Extract station ID from url
     * Select station when station ID is present
     */
    selectStationFromUrl(): void {
        const {params} = this.props;
        if (!params || !params.objectId) {
            return;
        }

        const stationNo = params.objectId;
        const stationSearch = new StationFinder(
            this.appStore,
            this.kiwis,
            this.messdatenConfig.layers,
            this.mapStore,
            this.uiStore
        );
        stationSearch.searchAndShowStation(stationNo, this.appStore.subtopic as TopicConfiguration);
        this.appStore.setObjectId(params.objectId);
    }

    handleResize(): void {
        this.checkUiSize();
    }
    checkUiSize(): void {
        const uiHeight = this.appUiRef.current?.clientHeight;
        const uiWidth = this.appUiRef.current?.clientWidth;
        const isSmallDevice = (window.innerWidth <= this.mapViewerConfig.breakpoints.md);
        this.uiStore.setUiSize(uiWidth, uiHeight, isSmallDevice);
    }
    componentDidMount(): void {
        // store route information to mobx store
        this.appStore.setRoute(this.props.location, this.props.params, this.props.navigate);
        this.setTopicFromUrl();
        this.selectStationFromUrl();

        this.mapStore.setWebMap(this.mapViewerConfig.webMap);

        // rerun until navigate is available
        // eslint-disable-next-line no-shadow
        autorun((reaction: any) => {
            const location = this.appStore.location;
            if (!location) {
                return;
            }
            this.handleBrowserNavigation();
            this.checkUiSize();
            reaction.dispose(); // stop autorun
        });
        autorun((reaction: any) => {
            const view = this.mapStore.view;
            if (!view) {
                return;
            }
            this.processAdditionalParams();
            this.checkUiSize();
            reaction.dispose(); // stop autorun
        });

        // close station popup on topic switch
        // noinspection JSUnusedLocalSymbols
        this.disposeStationPopupCloser = reaction(
            () => this.appStore.subtopic,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-shadow
            (subtopic) => {
                const stationPopup = this.uiStore.widgets.get("stationPopup") as StationPopup;
                if (stationPopup) {
                    stationPopup.hide();
                }
            }
        );
        this.checkUiSize();
        window.addEventListener("resize", this.handleResize);
        this.updateMessages();
        this.autorunDisposers.push(reaction(() => this.mapStore.activeLayers, () => {
            this.updateMessages();
        }));
    }
    componentDidUpdate(prevProps: AppProps): void {
        if(prevProps.location.pathname !== this.props.location.pathname
            || (this.props.location.hash && prevProps.location.hash!==this.props.location.hash)){
            this.appStore = this.props.appStore;
            this.handleBrowserNavigation();
        }
    }
    componentWillUnmount(): void {
        // dispose reactions
        this.kiwis.disposeStationsLoadedReaction();
        this.kiwis.disposeVisibleTimeSeriesListReaction();
        this.kiwis.disposeTimeSeriesListReaction();
        this.kiwis.disposeTimeSeriesPeriodReaction();
        this.kiwis.disposeThresholdSelectedReaction();

        this.disposeStationPopupCloser();
        this.disposeTopicChangeReaction();

        this.stopBrowserNavigationListener();
        window.removeEventListener("resize", this.handleResize);
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
    }

    setSidebarVisible(visible: boolean): void {
        this.uiStore.setSidebarVisible(visible);
    }

    render(): JSX.Element | null {
        let mapProperties: __esri.WebMapProperties | undefined;
        const {maintenance, search} = this.appConfig;
        const {menuVisible, isSidebarVisible, isSmallDevice, topicswitcherVisible, uiHeight} = this.uiStore;
        const {basemapOpacity, status, webMap} = this.mapStore;
        const {northFacing, alertMessages, startupMessage} = this.state;
        if (webMap){
            mapProperties = webMap as __esri.WebMapProperties;
        }
        let addClass = "geo-menu-" + ((menuVisible && !topicswitcherVisible)? "visible": "hidden");
        addClass += " geo-subtopic-menu-" + (topicswitcherVisible? "visible": "hidden");
        addClass += (this.appStore.topic)? " topic-" + this.appStore.topic.id : "";
        addClass += (this.appStore.subtopic)? " subtopic-" + this.appStore.subtopic.id : "";
        addClass += " " + status;
        addClass += (northFacing? " geo-north-facing": " geo-not-north-facing");
        addClass += (this.uiStore.sidebarHigh)? " geo-sidebar-is_high" : "";
        addClass += " "+this.state.addClass.join(" ");
        addClass += isSmallDevice? " geo-small_device" : "";
        addClass += (basemapOpacity <= 5)? " show-grid": "";
        const cssProperties = { "--uiHeight": uiHeight + "px" };
        return(
            <ErrorBoundary>
                <SemanticSidebar.Pushable className={addClass} style={cssProperties}>
                    <SemanticSidebar
                        animation="push"
                        onHide={() => this.setSidebarVisible(false)}
                        visible={isSidebarVisible}
                    >
                        <MediaQuery maxWidth={this.mapViewerConfig.breakpoints.md}>
                            <div className="mobilenavigation__header">
                                <button className="no-focusborder offside-button mobilenavigation__close-button"
                                    type="button"
                                    onClick={() => this.setSidebarVisible(false)}>
                                    <span className="sprite sprite--cross">
                                        <SVG src={crossIcon} />
                                    </span>
                                </button>
                            </div>
                            <Sidebar
                                appConfig={this.appConfig}
                                appStore={this.appStore}
                                mapStore={this.mapStore}
                                uiStore={this.uiStore}
                                config={this.mapViewerConfig.sidebar}
                                messdatenConfig={this.messdatenConfig}
                                small={true}
                            />
                        </MediaQuery>
                    </SemanticSidebar>
                    <SemanticSidebar.Pusher id={"map-root"} className={"geo-app-body"}>
                        <React.Suspense fallback={
                            <div className="pre-loader">
                                <div className="pre-loader-inner">Anwendung wird geladen ...</div>
                            </div>
                        }>
                            <div className="pre-loader">
                                <div className="pre-loader-inner">Anwendung wird geladen ...</div>
                            </div>
                            <WebMap
                                mapProperties={mapProperties}
                                viewProperties={this.appConfig.mapView as __esri.MapViewProperties}
                                onLoad={this.handleMapLoad}
                                store={this.mapStore}
                                uiStore={this.uiStore} >
                                <LayerStack
                                    kiwis={this.kiwis}
                                    mapStore={this.mapStore}
                                    appStore={this.appStore}
                                    uiStore={this.uiStore}
                                    config={this.mapViewerConfig.layers}
                                />
                            </WebMap>
                        </React.Suspense>
                        <div className={"geo-app geo-header-added"} ref={this.appUiRef}>
                            <Maintenance config={maintenance} />
                            <Header view={this.mapStore.view}
                                appStore={this.appStore}
                                uiStore={this.uiStore}
                                mapStore={this.mapStore}
                                capabilities={["search"]}
                                appConfig={this.appConfig}
                                searchConfig={search}
                                messdatenConfig={this.messdatenConfig}
                            />
                            <div className={"geo-ui-root"}>
                                <div className={"geo-ui"}>
                                    <div className={"geo-ui-inner-container geo-ui-manual-container"}>
                                        <StationPopup mapStore={this.mapStore}
                                            uiStore={this.uiStore}
                                            kiwis={this.kiwis}
                                            config={this.messdatenConfig.stationPopup}
                                            chartConfig={this.messdatenConfig.chart}/>
                                        {this.messdatenConfig.popup.disabled ? null : (
                                            <Popup
                                                appStore={this.appStore}
                                                config={this.mapViewerConfig.popup}
                                                mapViewerConfig={this.mapViewerConfig}
                                                appConfig={this.appConfig}
                                                view={this.mapStore.view}
                                                uiStore={this.uiStore}
                                                mapStore={this.mapStore}/>)
                                        }
                                    </div>
                                    <div className={"geo-ui-inner-container geo-ui-corner-container"}>
                                        <MediaQuery minWidth={(this.mapViewerConfig.breakpoints.md + 1)}>
                                            <Sidebar
                                                appConfig={this.appConfig}
                                                appStore={this.appStore}
                                                mapStore={this.mapStore}
                                                uiStore={this.uiStore}
                                                messdatenConfig={this.messdatenConfig}
                                                config={this.messdatenConfig.sidebar} />
                                            <TopicSwitcher
                                                appStore={this.appStore}
                                                mapStore={this.mapStore}
                                                uiStore={this.uiStore}
                                                topics={this.mapViewerConfig.subtopics}
                                                mapviewerConfig={this.mapViewerConfig} />
                                        </MediaQuery>
                                        <div className={"geo-ui-top-left geo-ui-corner"}>
                                            <ElevationProfile
                                                appStore={this.appStore}
                                                config={this.mapViewerConfig.tools?.profile}
                                                uiStore={this.uiStore}
                                                view={this.mapStore.view} />
                                            <Measurement
                                                appStore={this.appStore}
                                                uiStore={this.uiStore}
                                                view={this.mapStore.view} />
                                        </div>
                                        <div className={"geo-ui-top-right geo-ui-corner"}/>
                                        <div className={"geo-ui-bottom-right geo-ui-corner"}>
                                            <BasemapGallery config={this.appConfig.basemapGallery}
                                                view={this.mapStore.view}
                                                uiStore={this.uiStore}
                                                mapStore={this.mapStore} />
                                        </div>
                                        <div className={"geo-ui-bottom-left geo-ui-corner"}>
                                            <KiwisStatus kiwis={this.kiwis}/>
                                            { alertMessages?.length > 0 &&
                                                <div className="geo-messages">{alertMessages}</div>}
                                        </div>
                                        {startupMessage}
                                    </div>
                                </div>
                            </div>
                            <Footer view={this.mapStore.view}
                                appStore={this.appStore}
                                appConfig={this.appConfig}
                                uiStore={this.uiStore}
                                mapStore={this.mapStore}
                                mapViewerConfig={this.mapViewerConfig} />
                        </div>
                        <MessageBox store={this.appStore} />
                        <CookieBanner mapViewerConfig={this.mapViewerConfig} />
                    </SemanticSidebar.Pusher>
                </SemanticSidebar.Pushable>
            </ErrorBoundary>
        );
    }

    // noinspection JSUnusedLocalSymbols
    handleMapLoad(map: __esri.Map, view: __esri.MapView | __esri.SceneView): void {
        // TODO: AppState status erst auf complete schalten, wenn alle Widgets geladen sind
        // this.appStore.setState({ status: "complete" });
        if (this.mapViewerConfig.locateButtonEnabled) {
            const locateWidget = new EsriLocate({
                view
            });
            view.ui.add(locateWidget, "top-right");
        }
        // mode zoom buttons to top right
        view.ui.move("zoom", "top-right");
        const compassWidget = new EsriCompass({
            view
        });
        view.ui.add(compassWidget, "top-right");
        view.watch("rotation", (newValue: number)=>{
            this.setState({northFacing: newValue === 0});
        });

        view.closePopup();
    }

    /**
     * Register listener to set topic, when navigation is done with browser back/forward buttons
     */
    handleBrowserNavigation(): void {
        const { location, params } = this.props;
        // store route information to mobx store
        this.appStore.setRoute(location, params, this.props.navigate);
        if(!this.appStore.subtopic || !this.appStore.subtopic.routes.includes("/"+params.subtopic)){
            const subtopic = this.mapViewerConfig.getTopicByRoute(
                params.subtopic);
            if(subtopic) {
                this.appStore.setSubtopic(subtopic);
            }
        }
        if(params.objectId !== this.appStore.objectId){
            this.selectStationFromUrl();
        }
    }

    processAdditionalParams(): void {
        const {additionalParameter} = this.mapViewerConfig;
        if (!additionalParameter) {
            return;
        }
        const searchParamsLower = new URLSearchParams();
        for (const [name, value] of this.props.searchParams) {
            searchParamsLower.append(name.toLowerCase(), value);
        }
        const focus = searchParamsLower.get("focus");
        const marker = searchParamsLower.get("marker");
        const basemap = searchParamsLower.get("basemap");
        const bfs = searchParamsLower.get("bfs");
        const egrid = searchParamsLower.get("egrid");
        const parcel = searchParamsLower.get("parcel");
        const building = searchParamsLower.get("building");
        if (focus) {
            const focusArgs = focus.split(/\D/);
            if (focusArgs.length === 3 ) {
                this.focusArgs = [parseInt(focusArgs[0], 10),
                    parseInt(focusArgs[1], 10),
                    parseInt(focusArgs[2])
                ];
                delete this.appConfig.mapView.zoom;
                delete this.appConfig.mapView.viewpoint;
                this.appConfig.mapView.center = new EsriPoint({
                    x: this.focusArgs[0],
                    y: this.focusArgs[1],
                    spatialReference: this.appConfig.mapView.spatialReference
                });
                this.appConfig.mapView.scale = this.focusArgs[2];
                reactiveUtils.when(
                    () => this.mapStore.view?.ready,
                    () => {
                        const geometry = new EsriPoint({
                            x: this.focusArgs[0],
                            y: this.focusArgs[1],
                            spatialReference: this.appConfig.mapView.spatialReference
                        });
                        // @ts-ignore
                        this.mapStore.view.scale =this.focusArgs[2];
                        // @ts-ignore
                        this.mapStore.view.goTo({geometry, scale: this.focusArgs[2]}).catch((error) => {
                            if (error.name !== "view:goto-interrupted") {
                                // eslint-disable-next-line no-console
                                console.error(error);
                            }
                        });
                        if (marker !== null) {
                            let symbol;
                            if (additionalParameter.marker
                                && additionalParameter.marker[marker.toLowerCase()]) {
                                symbol = additionalParameter.marker[marker.toLowerCase()];
                            } else {
                                symbol = this.appConfig.search.resultSymbol?.pointer;
                            }
                            if (symbol) {
                                // @ts-ignore
                                this.mapStore.view.graphics.push(
                                    new EsriGraphic({
                                        geometry,
                                        symbol
                                    })
                                );
                            }
                        }
                    },
                    {initial: true, once: true}
                );
            }
        } else if (bfs || egrid || parcel || building) {
            // TODO: Code überarbeiten und mit Search abstimmen
            const sourceConfig = this.appConfig.search.sources.find(
                (s: SearchSourceConfiguration) =>
                    s.name === (bfs? "Gemeinden, Orte, Flurnamen" : building?
                        "Gebäudeversicherungsnummern GVL" : parcel?
                            "Grundstücke": "Eidg. Grundstückidentifikator EGRID"));
            autorun((reaction: any) => {
                const popup = this.uiStore.widgets.get("popup") as Popup;
                reaction.dispose(); // stop autorun
                if (sourceConfig) {
                    let searchLayer: FeatureLayer | __esri.Sublayer | undefined;
                    let queryConfig: SearchSourceQueryConfiguration | undefined;
                    if (bfs && sourceConfig.queries) {
                        queryConfig = sourceConfig.queries.find((q) => q.id === "GRZGEMLU");
                    }
                    if (!queryConfig && sourceConfig.query) {
                        queryConfig = sourceConfig.query;
                    }
                    const searchLayerPromises: Array<Promise<FeatureLayer | __esri.Sublayer | undefined>> = [];
                    if (sourceConfig.query?.id === "AVLSPRZX" && popup && popup.avlsLayer) {
                        searchLayerPromises.push(new Promise((resolve) => {
                            popup.avlsLayer?.load().then(()=> {
                                if (popup.avlsLayer?.hasOwnProperty("allSublayers")) {
                                    searchLayer = (popup.avlsLayer as __esri.MapImageLayer).allSublayers.getItemAt(0);
                                } else {
                                    searchLayer = popup.avlsLayer as FeatureLayer;
                                }
                                resolve(searchLayer);
                            });
                        }));
                    } else if (queryConfig?.portalItem){
                        const layerConfig = {
                            layerType: "feature",
                            layerId: (queryConfig.layerId) ? queryConfig.layerId : undefined,
                            portalItem: queryConfig.portalItem,
                            opacity: 1,
                            listMode: "hide",
                            legendEnabled: false
                        };
                        searchLayerPromises.push(new Promise((resolve) => {
                            searchLayer = new FeatureLayer(layerConfig as __esri.FeatureLayerProperties);
                            resolve(searchLayer);
                        }));
                        if (!bfs && sourceConfig.queries) {
                            sourceConfig.queries.forEach((q)=>{
                                searchLayerPromises.push(new Promise((resolve) => {
                                    if (q.portalItem) {
                                        layerConfig.layerId = (q.layerId) ? q.layerId : undefined;
                                        layerConfig.portalItem = q.portalItem;
                                        searchLayer = new FeatureLayer(layerConfig as __esri.FeatureLayerProperties);
                                        resolve(searchLayer);
                                    }
                                }));
                            });
                        }
                    }
                    Promise.all(searchLayerPromises).then((searchLayers) => {
                        if (searchLayer && queryConfig) {
                            let where;
                            if (parcel) {
                                where = parcel.split(/[^\d.]/).map(
                                    (no) => {
                                        const [gbcode, prznr] = no.split(".");
                                        return prznr? queryConfig?.expression.replace("$feature.gbcode",
                                            ""+parseInt(gbcode, 10)).replace("$feature.prznr", ""+parseInt(prznr, 10))
                                            .replace(/[$`{}]/g,"") : null;
                                    }
                                ).join(" OR ");
                            } else if (egrid) {
                                where = egrid.split(/\DCH/i).map(
                                    (no) => queryConfig?.expression.replace("$feature.egrid",
                                        "CH" + parseInt(no.replace(/\D/g, ""), 10))
                                        .replace(/[$`{}]/g,"")
                                ).join(" OR ");
                            } else if (bfs) {
                                where = bfs.split(/\D/i).map(
                                    (no) => queryConfig?.expression.replace("$feature.bfsnr",
                                        ""+parseInt(no, 10))
                                        .replace(/[$`{}]/g,"")
                                ).join(" OR ");
                            } else if (building) {
                                where = building.split(/[^\d.a-z]/i).map(
                                    (no) => {
                                        const [gbcode, gvnr] = no.split(".");
                                        return gvnr? queryConfig?.expression.replace("$feature.gbcode",
                                            ""+parseInt(gbcode, 10)).replace("$feature.gvnr", gvnr)
                                            .replace(/[$`{}]/g,"") : null;
                                    }
                                ).join(" OR ");
                            }
                            if (typeof where === "string") {
                                const queryPromises: Array<Promise<__esri.FeatureSet | undefined>> = [];
                                let resultFeatures: __esri.Graphic[] = [];
                                const query = searchLayer.createQuery();
                                query.where = where;
                                searchLayers.forEach((sLayer)=>{
                                    queryPromises.push(new Promise((resolve) => {
                                        if(sLayer) {
                                            sLayer.queryFeatures(query).then((response) => {
                                                if (response.features.length > 0) {
                                                    resultFeatures = resultFeatures.concat(response.features);
                                                }
                                                resolve(response);
                                            });
                                        }
                                    }));
                                });
                                Promise.all(queryPromises).then(() => {
                                    if (resultFeatures.length === 0) {
                                        console.warn("Kein Resultat für URL Parameter gefunden");
                                        return;
                                    }
                                    if (this.appConfig.search.resultSymbol) {
                                        let highlightSymbol: FillSymbolConfiguration | PictureMarkerSymbolConfiguration;
                                        let scale: number | undefined;
                                        if (resultFeatures[0].geometry.type === "polygon"){
                                            highlightSymbol = this.appConfig.search.resultSymbol.highlight;
                                        } else {
                                            highlightSymbol = this.appConfig.search.resultSymbol.pointer;
                                            // scale definieren falls nur ein Punkt
                                            if (resultFeatures.length === 1) {
                                                scale = 1128;
                                            }
                                        }
                                        const geometries: __esri.Geometry[] = [];
                                        resultFeatures.forEach((item) => {
                                            this.mapStore?.view?.graphics.push(
                                                new EsriGraphic({
                                                    geometry: item.geometry,
                                                    symbol: highlightSymbol
                                                })
                                            );
                                            geometries.push(item.geometry);
                                        });
                                        let gotoTarget;
                                        if (scale) {
                                            gotoTarget = {target: geometries, scale};
                                        }else{
                                            gotoTarget = {target: geometries};
                                        }
                                        this.mapStore.view?.goTo(gotoTarget).catch((error) => {
                                            if (error.name !== "view:goto-interrupted") {
                                                // eslint-disable-next-line no-console
                                                console.error(error);
                                            }
                                        });
                                    }
                                });
                            }
                        }
                    });
                }
            });
        }
        if (basemap) {
            let basemapTag: string;
            if (additionalParameter?.basemap && additionalParameter.basemap[basemap]) {
                basemapTag = additionalParameter.basemap[basemap];
            } else {
                basemapTag = "MV6:" + basemap.replace(/mv6:/i, "").toLowerCase();
            }
            autorun((r: any) => {
                if (!this.mapStore.view || !this.mapStore.map) {
                    return;
                }
                r.dispose(); // stop autorun
                this.mapStore.setBasemap(basemapTag);
            });
        }
    }
    updateMessages(){
        const startupMessage = this.prepareMessagesContent(
            this.mapViewerConfig.meta?.startupMessage?
                [this.mapViewerConfig.meta.startupMessage]: undefined, true);
        Promise.all(startupMessage).then((msgs) => {
            this.setState({startupMessage: msgs.filter((m) => m)});
        });
        const alertMessages = this.prepareMessagesContent(this.mapViewerConfig.meta.alerts);
        Promise.all(alertMessages).then((msgs) => {
            this.setState({alertMessages: msgs.filter((m) => m)});
        });
    }
    prepareMessagesContent(messagesConfig?: MessageConfiguration[], startup=false):
        Array<Promise<JSX.Element | undefined>> {
        const messagePromises: Array<Promise<JSX.Element | undefined>> = [];
        if (messagesConfig && Array.isArray(messagesConfig)) {
            const profile: __esri.Profile = {
                variables: [{
                    name: "$subtopicId",
                    type: "text"
                }, {
                    name: "$activeLayers",
                    type: "array",
                    elementType: {
                        type: "text"
                    }
                }]
            };
            messagesConfig.forEach((message, index) => {
                if (typeof message.condition === "undefined") {
                    messagePromises.push(new Promise((resolve) => {
                        resolve(this.prepareMessageContent(message, index, startup));
                    }));
                } else {
                    if(this.mapStore.activeLayers) {
                        const arcadeExecutor = arcade.createArcadeExecutor(message.condition, profile);
                        messagePromises.push(new Promise((resolve) => {
                            arcadeExecutor.then((ae) => {
                                const condition = ae.execute({
                                    $activeLayers: this.mapStore.activeLayers,
                                    $subtopicId: (this.appStore.subtopicId? this.appStore.subtopicId: "")
                                });
                                let m: JSX.Element | undefined;
                                if (condition) {
                                    m = this.prepareMessageContent(message, index, startup);
                                }
                                resolve(m);
                            });
                        }));
                    }
                }
            });
        }
        return messagePromises;
    }
    prepareMessageContent(messageConfig: MessageConfiguration, index: number, startup=false): JSX.Element {
        let message: JSX.Element;
        if (startup){
            message = <MessageStartup
                key={index}
                topic={this.appStore.topic.id}
                header={messageConfig.title}
                className={messageConfig.class ? messageConfig.class : ""}
                content={messageConfig.content}
                appConfig={this.appConfig}/>;
        } else {
            message = <MessageBlock
                key={index}
                index={index}
                topic={this.appStore.topic.id}
                header={messageConfig.title}
                className={messageConfig.class ? (messageConfig as MessageProps).class : undefined}
                content={messageConfig.content}
                appConfig={this.appConfig}/>;
        }
        return message;
    }

    /**
     * Reaction for topic changes
     * Used to update group layer visibility after topic changes.
     */
    disposeTopicChangeReaction = reaction(
        () => this.props.appStore.subtopic,
        (subtopic: TopicConfiguration) => {
            // activate group layers included in appStore.subtopic?.layers.visible
            // do it here, because there is no specific LayerFilter on these group layers
            this.mapStore?.groupLayers.forEach((gl: __esri.Layer) => {
                gl.visible = !!subtopic?.layers.visible.some((l: TopicLayerConfiguration) => l.id === gl.id);
            });
        }
    );

}
